/**
 * TicketDetailHeader - Header (identidade + ações) do detalhe do ticket,
 * compartilhado entre a página (rota) e o TicketDetailDrawer.
 *
 * Anatomia por variant:
 *  - `page`: esquerda = breadcrumb (Help Desk › Tickets — NUNCA seta-voltar) +
 *    chip do número + botão de editar título (→ modal, DS 1.9, nunca inline);
 *    direita = ações (Transferir, Pausar/Retomar, Resolver, Fechar, Reabrir,
 *    overflow ⋮).
 *  - `drawer` (DS 2026-08-11): SEM breadcrumb — gramática `.drw-head` do drawer
 *    de Detalhes do Lead: ícone do módulo + título + badge do ticketNumber com
 *    copy-to-clipboard à esquerda; ações (`DrawerHeaderButton` 30px) + X (via
 *    `onCloseDrawer`) à direita, tudo na MESMA barra. SEM badges bar — etapa/
 *    prioridade/tags/projeto vivem na aba Detalhes (como no drawer de lead).
 *  - Mobile (DS 2026-08-11, ambos variants): as ações se agrupam num único
 *    kebab (•••, `CardActionsMenu`) — nunca fileira de icon-buttons.
 *  - Badges bar (só na `page`): etapa, pipeline, prioridade e projeto (faixa
 *    dedicada abaixo da toolbar).
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Breadcrumb, CardActionsMenu, DrawerHeaderButton, Tooltip, type CardAction } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { useIsMobile } from '@/shared/hooks';
import type { IconName } from '@/shared/icons';
import { useProjectsDropdown } from '@/modules/helpdesk/projects/hooks';
import { usePipelinesDropdown } from '@/modules/helpdesk/pipelines/hooks/usePipelines';
import { useDeleteTicket } from '../hooks/useTickets';
import { TransferTicketDialog } from './TransferTicketDialog';
import { EditTitleModal } from './EditTitleModal';
import type { PopulatedTicket } from '../types';
import type { TicketPriority, TicketStageResponse, TicketStatusCategory } from '../types';
import { TICKET_PRIORITY } from '../types';
import { Icon } from '@/shared/icons'
/** Ação inline do header — modelo compartilhado pelos renders page/drawer/mobile. */
interface HeaderAction {
  label: string;
  icon: IconName;
  prim?: boolean;
  onClick: () => void;
  testid?: string;
}

export interface TicketDetailHeaderProps {
  ticket: PopulatedTicket;
  onPause: (stageId?: string) => void;
  onResume: (stageId?: string) => void;
  /** Abre o `ResolveTicketModal` (página) — "Resolver" não usa mais o
   * seletor de stage direto, mas o fluxo dedicado via `useResolveTicket`. */
  onResolveClick: () => void;
  onClose: (stageId?: string) => void;
  onReopen: (stageId?: string) => void;
  onTitleChange: (title: string) => void;
  /** Mesma confirmação usada pela sidebar ao cruzar categoria de stage —
   * usada aqui quando há 2+ etapas candidatas pra Pausar/Retomar/Fechar/Reabrir. */
  onStageChangeWithConfirmation: (stageId: string, stage: TicketStageResponse) => void;
  stages: TicketStageResponse[];
  loading?: boolean;
  /** `page` (default) = breadcrumb + chip do número; `drawer` = ícone do módulo +
   * badge do ticketNumber com copy, sem breadcrumb (DS 2026-08-11). */
  variant?: 'page' | 'drawer';
  /** Fecha o drawer que hospeda o header — renderiza o X na mesma barra das ações
   * (só faz sentido com `variant="drawer"`). */
  onCloseDrawer?: () => void;
}

export const TicketDetailHeader: React.FC<TicketDetailHeaderProps> = ({
  ticket,
  onPause,
  onResume,
  onResolveClick,
  onClose,
  onReopen,
  onTitleChange,
  onStageChangeWithConfirmation,
  stages,
  loading = false,
  variant = 'page',
  onCloseDrawer
}) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const deleteMutation = useDeleteTicket();
  const isMobile = useIsMobile();

  // Projeto vinculado (rota dropdown cross-module, sem permissão de módulo).
  const { data: projects } = useProjectsDropdown();
  const linkedProject = useMemo(
    () => (ticket.projectId ? projects?.find((p) => p.id === ticket.projectId) : undefined),
    [projects, ticket.projectId],
  );

  // Pipeline vinculado (badge da badges bar).
  const { data: pipelines } = usePipelinesDropdown();
  const linkedPipeline = useMemo(
    () => (ticket.pipelineId ? pipelines?.find((p) => p.id === ticket.pipelineId) : undefined),
    [pipelines, ticket.pipelineId],
  );

  const [actionPopover, setActionPopover] = useState<{
    type: 'pause' | 'resume' | 'close' | 'reopen';
    stages: TicketStageResponse[];
  } | null>(null);

  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [isEditTitleOpen, setIsEditTitleOpen] = useState(false);

  // Copy do ticketNumber (badge do variant drawer) — ícone copy → check por 2s,
  // sem toast (padrão WebhooksTab).
  const [copiedNumber, setCopiedNumber] = useState(false);
  const handleCopyTicketNumber = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(ticket.ticketNumber);
      setCopiedNumber(true);
      setTimeout(() => setCopiedNumber(false), 2000);
    } catch {
      // Fallback silencioso
    }
  };

  const category = ticket.statusCategory;
  const isWaiting = category === 'waiting';
  const isTerminal = category === 'resolved' || category === 'closed';

  const stageColor = ticket.stage?.color || '#6B7280';
  const stageName = ticket.stage?.name || ticket.status || '-';

  const priorityColor = TICKET_PRIORITY[ticket.priority as TicketPriority]?.color || '#6B7280';
  const priorityLabel = TICKET_PRIORITY[ticket.priority as TicketPriority]?.label || ticket.priority;

  const handleActionClick = (
    type: 'pause' | 'resume' | 'close' | 'reopen',
    targetCategory: TicketStatusCategory,
    callback: (stageId?: string) => void
  ) => {
    const matchingStages = stages.filter(s => s.category === targetCategory);
    if (matchingStages.length <= 1) {
      callback(matchingStages[0]?.id);
    } else {
      setActionPopover({ type, stages: matchingStages });
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ message: 'Tem certeza que deseja excluir este ticket?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    try {
      await deleteMutation.mutateAsync(ticket.id);
      toast.success('Ticket excluído!');
      navigate('/helpdesk/tickets');
    } catch {
      toast.error('Erro ao excluir ticket');
    }
  };

  const goToList = () => navigate('/helpdesk/tickets');

  // Modelo ÚNICO das ações do header — os três renders (Button na page,
  // DrawerHeaderButton no drawer, kebab no mobile) derivam daqui, garantindo
  // paridade de condições/rotulagem entre superfícies.
  const inlineActions: HeaderAction[] = [
    { label: 'Transferir', icon: 'refresh', onClick: () => setIsTransferDialogOpen(true), testid: 'ticket-detail-transfer-button' },
    ...(!isTerminal
      ? [
          isWaiting
            ? { label: 'Retomar', icon: 'play', onClick: () => handleActionClick('resume', 'in_progress', onResume) }
            : { label: 'Pausar', icon: 'pause', onClick: () => handleActionClick('pause', 'waiting', onPause) },
          { label: 'Resolver', icon: 'check', prim: true, onClick: onResolveClick, testid: 'ticket-detail-resolve-button' },
        ] satisfies HeaderAction[]
      : []),
    ...(category === 'resolved'
      ? [{ label: 'Fechar', icon: 'archive', prim: true, onClick: () => handleActionClick('close', 'closed', onClose) } satisfies HeaderAction]
      : []),
    ...(isTerminal
      ? [{ label: 'Reabrir', icon: 'refresh', onClick: () => handleActionClick('reopen', 'in_progress', onReopen) } satisfies HeaderAction]
      : []),
  ];

  // Mobile (DS 2026-08-11): TODAS as ações do header agrupadas num único kebab
  // ao lado do X — nunca fileira de icon-buttons.
  const mobileActions: CardAction[] = [
    ...inlineActions.map((a) => ({ label: a.label, icon: a.icon, disabled: loading, onClick: a.onClick })),
    {
      label: 'Excluir ticket',
      icon: 'trash',
      variant: 'danger',
      dividerBefore: true,
      disabled: deleteMutation.isPending,
      onClick: () => { void handleDelete(); },
    },
  ];

  // Lápis de editar título → modal (DS 1.9) — mesmo botão nas duas variants.
  const editTitleButton = (
    <button
      type="button"
      onClick={() => setIsEditTitleOpen(true)}
      className="p-1.5 rounded-lg transition-colors flex-shrink-0"
      style={{ color: 'var(--theme-muted-text)' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      disabled={loading}
      title="Editar título"
      aria-label="Editar título"
      data-testid="ticket-detail-edit-title-button"
    >
      <Icon name="edit" className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <div
      className="sticky top-0 z-10"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        // Borda externa só na page (fecha a badges bar). No drawer a badges bar
        // não existe e a toolbar já tem a própria borda — duas ficariam
        // adjacentes (divisor de 2px vs 1px do lead).
        ...(variant === 'page' ? { borderBottom: '1px solid var(--theme-card-border)' } : {}),
      }}
    >
      {/* Toolbar (3 zonas: identidade à esquerda · ações à direita) */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-2.5"
        style={{ borderBottom: '1px solid var(--theme-card-border)' }}
      >
        {variant === 'drawer' ? (
          /* Left (drawer, DS 2026-08-11) — SEM breadcrumb: ícone do módulo +
             título + badge do ticketNumber com copy. Mesma gramática `.drw-head`
             do drawer de Detalhes do Lead. */
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2 rounded-lg flex-none" style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}>
              <Icon name="flag" className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center min-w-0">
                <h2
                  className="text-[16px] font-bold whitespace-nowrap truncate"
                  style={{ color: '#1e293b' }}
                  data-testid="ticket-detail-title"
                >
                  {ticket.title}
                </h2>
                {editTitleButton}
              </div>
              {/* Badge do id do chamado — número completo + copy to clipboard */}
              <span
                className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-full text-xs font-bold font-mono"
                style={{ backgroundColor: '#f1f5f9', color: '#334155' }}
                data-testid="ticket-detail-number"
              >
                <Icon name="hash" className="w-3 h-3" />
                {ticket.ticketNumber}
                {/* Tooltip shared (Radix) — NUNCA `title=` nativo (DS F80) */}
                <Tooltip content="Copiar ID do chamado">
                  <button
                    type="button"
                    onClick={() => { void handleCopyTicketNumber(); }}
                    className="p-1 rounded-full transition-colors"
                    style={{ color: copiedNumber ? '#059669' : '#64748b' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    aria-label="Copiar ID do chamado"
                    data-testid="ticket-detail-number-copy"
                  >
                    <Icon name={copiedNumber ? 'check' : 'copy'} className="w-3 h-3" />
                  </button>
                </Tooltip>
              </span>
            </div>
          </div>
        ) : (
          /* Left (page) — breadcrumb (F25-F31: shared, SEM seta-voltar; mobileLegend
             no mobile) + número + editar. O segmento final da trilha é o título. */
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="min-w-0">
              <Breadcrumb
                items={[
                  { label: 'Help Desk', onNavigate: goToList, testid: 'ticket-detail-breadcrumb-helpdesk' },
                  { label: 'Tickets', onNavigate: goToList, testid: 'ticket-detail-breadcrumb-tickets' },
                  { label: ticket.title, testid: 'ticket-detail-title' },
                ]}
                mobileLegend="Help Desk · Tickets"
                data-testid="ticket-detail-breadcrumb"
              />
            </div>

            {/* Número do ticket — CURTO como o mockup (# 4821); completo no tooltip */}
            <span
              className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-bold font-mono flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-button-ghost)', color: 'var(--theme-card-text)' }}
              title={`#${ticket.ticketNumber}`}
              data-testid="ticket-detail-number"
            >
              <Icon name="hash" className="w-3 h-3" />
              {String(parseInt(ticket.ticketNumber?.split('-').pop() || '', 10) || ticket.ticketNumber)}
            </span>

            {editTitleButton}
          </div>
        )}

        {/* Right — ações (+ X quando hospedado em drawer) */}
        <div className="relative flex items-center gap-2 flex-shrink-0">
          {isMobile ? (
            /* Mobile (DS 2026-08-11): kebab único agrupa TODAS as ações —
               nunca fileira de icon-buttons ao lado do X. */
            <CardActionsMenu actions={mobileActions} data-testid="ticket-detail-more-menu-button" />
          ) : (
            <>
              {variant === 'drawer'
                ? /* Drawer: DrawerHeaderButton (30px/12.5, padrão do drawer de
                     Detalhes do Lead — DS 2026-08-11) */
                  inlineActions.map((a) => (
                    <DrawerHeaderButton
                      key={a.label}
                      icon={a.icon}
                      label={a.label}
                      prim={a.prim}
                      onClick={a.onClick}
                      disabled={loading}
                      data-testid={a.testid}
                    />
                  ))
                : /* Page: Button shared (toolbar de página) */
                  inlineActions.map((a) => (
                    <Button
                      key={a.label}
                      onClick={a.onClick}
                      variant={a.prim ? 'primary' : 'secondary'}
                      size="sm"
                      icon={a.icon}
                      iconPosition="left"
                      disabled={loading}
                      data-testid={a.testid}
                    >
                      {a.label}
                    </Button>
                  ))}

              {/* Overflow menu (⋮) — kebab padrão do DS (F78) */}
              <CardActionsMenu
                actions={[
                  {
                    label: 'Excluir ticket',
                    icon: 'trash',
                    variant: 'danger',
                    disabled: deleteMutation.isPending,
                    onClick: () => { void handleDelete(); },
                  },
                ]}
                data-testid="ticket-detail-more-menu-button"
              />
            </>
          )}

          {/* Action Popover for 2+ stages */}
          {actionPopover && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setActionPopover(null)} />
              <div
                className="absolute right-0 top-full mt-1 z-50 rounded-lg shadow-lg border py-1 min-w-[200px]"
                style={{ backgroundColor: '#ffffff', borderColor: '#e5e7eb' }}
              >
                <div className="px-3 py-1.5 text-xs font-medium" style={{ color: '#64748b' }}>
                  Selecionar etapa:
                </div>
                {actionPopover.stages.map((stage) => (
                  <button
                    key={stage.id}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                    style={{ color: '#1e293b' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onClick={() => {
                      // 2+ stages candidatas pra essa transição de categoria — mesma
                      // confirmação da sidebar (`StageChangeConfirmation`), em vez de
                      // aplicar a mudança direto como antes.
                      onStageChangeWithConfirmation(stage.id, stage);
                      setActionPopover(null);
                    }}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    {stage.name}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* X — fecha o drawer hospedeiro, na MESMA barra das ações (padrão do
              drawer de Detalhes do Lead) */}
          {variant === 'drawer' && onCloseDrawer && (
            <button
              onClick={onCloseDrawer}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors flex-none"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              aria-label="Fechar"
              data-testid="ticket-detail-drawer-close"
            >
              <Icon name="x" className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Badges bar — etapa · pipeline · prioridade · projeto. SÓ na page
          (DS 2026-08-11): em drawer de detalhe não há sub-header — esse
          contexto vive na aba Detalhes, como no drawer de lead. */}
      {variant === 'page' && (
      <div className="flex items-center gap-2 flex-wrap px-4 py-2">
        <span
          className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: `${stageColor}1f`, color: stageColor }}
          data-testid="ticket-detail-stage-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stageColor }} />
          {stageName}
        </span>

        {linkedPipeline && (
          <span
            className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: `${linkedPipeline.color}1f`, color: linkedPipeline.color }}
            title={`Pipeline: ${linkedPipeline.name}`}
            data-testid="ticket-detail-pipeline-badge"
          >
            <Icon name="list" className="w-3 h-3" />
            <span className="max-w-[180px] truncate">{linkedPipeline.name}</span>
          </span>
        )}

        <span
          className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold"
          style={{ backgroundColor: `${priorityColor}1f`, color: priorityColor }}
          data-testid="ticket-detail-priority-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: priorityColor }} />
          {priorityLabel}
        </span>

        {linkedProject && (
          <button
            type="button"
            onClick={() => navigate(`/helpdesk/projects/${linkedProject.id}`)}
            className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-semibold transition-opacity hover:opacity-80"
            style={{ backgroundColor: `${linkedProject.color}1f`, color: linkedProject.color }}
            title={`Projeto: ${linkedProject.name}`}
            data-testid="ticket-detail-project-badge"
          >
            <Icon name="folder" className="w-3 h-3" />
            <span className="max-w-[180px] truncate">{linkedProject.name}</span>
          </button>
        )}
      </div>
      )}

      {/* Editar título — modal (DS 1.9) */}
      <EditTitleModal
        isOpen={isEditTitleOpen}
        onClose={() => setIsEditTitleOpen(false)}
        currentTitle={ticket.title}
        onSave={onTitleChange}
        loading={loading}
      />

      {/* Transferir ticket — Modal M1 */}
      <TransferTicketDialog
        ticketId={ticket.id}
        ticketNumber={ticket.ticketNumber}
        ticketTitle={ticket.title}
        isOpen={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
        pipelineId={ticket.pipelineId}
      />
    </div>
  );
};
